import {FC, useEffect, useRef, useState} from 'react';

import * as sentry from '@sentry/react';
import {useTranslation} from 'react-i18next';

import {config} from 'config/env';
import useAnalytics, {
  ANALYTICS,
  BASKET_EDIT_SOURCE_EVENTS,
  SUGGESTEDLIST_CHANNELS,
  EVENT_SOURCE
} from 'contexts/analytics-context';
import {useAppSelector, useAppDispatch} from 'hooks';
import {createUserAddress, getUserAddresses, updateUserAddress} from 'services/address-strategy';
import MultiStoreService from 'services/multistore/multistore-service';
import ReactNative from 'services/react-native';
import SendBrowserEvent from 'services/send-browser-event';
import {fetchUserAddressesAction, setSelectedAddressAction} from 'store/actions/addresses-action';
import {setBasketQuantityChangedProductMapAction} from 'store/actions/basket-action';
import {
  CreateAddressInputType,
  UpdateAddressInputType,
  UserAddress,
  AddressTypeEnum,
  TPinMapSelectedLocation,
  DistanceResult,
  MapAddressCoords,
  UserAddressWithOptionalCity
} from 'types';
import {isCheckoutV3BasedStore, isCheckoutV3Enable} from 'utils';
import {getAddressStringForGeocoding, getCheckoutLatLng} from 'utils/address';
import {getDistanceFromCoordsToAddress, getSphericalDistance} from 'utils/googleMapHelper';
import {filter1PItems, filter3PItems, isSyncAddressEnabled, omit} from 'utils/helper';
import {AddressChangedModal} from 'views/basket-modals/AddressChangedModal';
import {LocationMismatchModal} from 'views/basket-modals/LocationMismatchModal';

import {AddressFormModal} from './AddressFormModal';

interface Props {
  modalOpen: boolean;
  setModalOpen?: any;
  address?: UserAddress | undefined;
  onFormSubmit: () => void;
  openFullMapView: boolean;
  setOpenFullMapView: React.Dispatch<React.SetStateAction<boolean>>;
  updateStoresForBasket: (address: UserAddress) => Promise<void>;
  initialPinMapData: TPinMapSelectedLocation;
  hasUserMovedMapManually: boolean;
  setAddressBeingEdited: (value: UserAddress) => void;
}

export const AddressFormView: FC<Props> = ({
  modalOpen,
  setModalOpen,
  address: addressBeingEdited,
  onFormSubmit: handleConfirmCallback,
  openFullMapView,
  setOpenFullMapView,
  updateStoresForBasket,
  initialPinMapData,
  hasUserMovedMapManually,
  setAddressBeingEdited
}) => {
  const dispatch = useAppDispatch();
  const {currentStore, isDirectCheckout} = useAppSelector((state) => state?.app);
  const {idToken, customerId} = useAppSelector((state) => state?.auth) || {};
  const {selectedShippingAddress, availableAddresses: userAddressList} = useAppSelector((state) => state?.addresses);
  const basket = useAppSelector((state) => state?.basket?.basket);
  const [loading, setLoading] = useState(false);

  const [isZipCodeChanged, setIsZipCodeChanged] = useState(false);

  const isReactNativeView = ReactNative.isReactNativeWebView();
  const showLocationMismatchWarning = config.IS_LOCATION_MISMATCH_NOTIFICATION_ENABLED;
  const isCheckoutV3Store = isCheckoutV3BasedStore(currentStore?.storeCodes[0] as string);
  const isCheckoutV3 = isCheckoutV3Enable() && isCheckoutV3Store;

  const ifCoordinatesAreChanged =
    addressBeingEdited &&
    (addressBeingEdited.user_latitude !== initialPinMapData?.coordinates?.lat ||
      addressBeingEdited.user_longitude !== initialPinMapData?.coordinates?.lng);

  const initialUserCoordinates = {
    user_latitude: initialPinMapData?.coordinates?.lat,
    user_longitude: initialPinMapData?.coordinates?.lng,
    metadata: {
      zoomLevel: `${initialPinMapData?.zoomLevel}`
    }
  };

  const initialZipAddress = {
    zip: currentStore?.zipcode,
    address1: config.IS_GOOGLE_ZIP_ENABLED ? initialPinMapData?.address || addressBeingEdited?.address1 : '',
    province_id: '',
    district_id: '',
    subDistrict_id: '',
    province: null,
    district: null,
    subDistrict: currentStore?.subdistrict
  };

  const initialAddress = addressBeingEdited
    ? {
        ...addressBeingEdited,
        ...initialUserCoordinates,
        ...(ifCoordinatesAreChanged ? initialZipAddress : {})
      }
    : {
        ...initialZipAddress,
        ...initialUserCoordinates,
        isDefault: !userAddressList?.length
      };

  const {t, i18n} = useTranslation();
  const analytics = useAnalytics();

  const isThai = i18n.language === 'th';

  const onCloseModal = (isSaved = false) => {
    setModalOpen(false, isSaved);
    setOpenFullMapView(false);
  };

  const onAddOrEditUserAddress = async (
    idToken: string,
    address: CreateAddressInputType | UpdateAddressInputType,
    addressId?: string
  ): Promise<UserAddress | undefined> => {
    try {
      let userAddress;
      if (addressId) {
        userAddress = await updateUserAddress(idToken, addressId, address, AddressTypeEnum.SHIPPING);
        SendBrowserEvent.showSuccessToast(t('message.editShippingAddressSuccessMsg'));
      } else {
        address.addressType = AddressTypeEnum.SHIPPING;
        userAddress = createUserAddress(idToken, address as CreateAddressInputType);
        SendBrowserEvent.showSuccessToast(t('message.addShippingAddressSuccessMsg'));
      }
      return userAddress as UserAddress;
    } catch (e) {
      sentry.captureException(e);
    }
  };

  const forceChangeAddrRef = useRef<(() => Promise<void>) | null>(null);

  const addressAcceptedLocationMismatchAnalytic = (mapCoords: MapAddressCoords, addressCoords: DistanceResult) => {
    analytics.trackEvent(ANALYTICS.ADDRESS_ACCEPTED_LOCATION_MISMATCH, {
      mapCoordinates: mapCoords.mapLatLng,
      mapAddressString: mapCoords.mapAddressString,
      addressCoordinates: addressCoords.latLng,
      addressString: addressCoords.addressString,
      distanceKm: addressCoords.distanceKm,
      channel: SUGGESTEDLIST_CHANNELS.MAKRO_PRO,
      segment_source_name: isReactNativeView ? EVENT_SOURCE.APP : EVENT_SOURCE.WEB
    });
  };

  const onChangeAddress = async (
    address: CreateAddressInputType,
    isShipping: boolean,
    _addressId?: string,
    forceSubmit?: boolean,
    closeAddressForm?: boolean
  ) => {
    try {
      if (!showLocationMismatchWarning && address.zip !== initialAddress.zip && !forceSubmit) {
        forceChangeAddrRef.current = () => onChangeAddress(address, isShipping, _addressId, true, !!closeAddressForm);

        setIsZipCodeChanged(true);
        sentry.setUser({id: customerId});
        sentry.captureMessage(
          `Zipcode changed from ${initialAddress.zip} to ${address.zip} for user - ${customerId}`,
          'error'
        );
        return;
      }

      const addressString = getAddressStringForGeocoding(address);
      const mapAddressString = getAddressStringForGeocoding(initialAddress as CreateAddressInputType);
      const mapLatLng = {lat: address.user_latitude ?? 0, lng: address.user_longitude ?? 0};
      const distanceResult = await getDistanceFromCoordsToAddress(mapLatLng, addressString);
      address.metadata = address.metadata || {};
      address.metadata.distanceKm = distanceResult?.distanceKm;

      if (_addressId && addressBeingEdited?.locus_latitude && addressBeingEdited?.locus_longitude) {
        const locusDistanceInKm = await getSphericalDistance(
          {
            lat: addressBeingEdited.locus_latitude as number,
            lng: addressBeingEdited.locus_longitude as number
          },
          distanceResult?.latLng
        );

        address.metadata.locusDistanceKm = locusDistanceInKm;
      }
      if (showLocationMismatchWarning) {
        try {
          setLoading(true);
          if (distanceResult.isDistanceBeyondThreshold) {
            address.latitude = distanceResult?.latLng?.lat;
            address.longitude = distanceResult?.latLng?.lng;
            if (!forceSubmit) {
              forceChangeAddrRef.current = () =>
                onChangeAddress(address, isShipping, _addressId, true, !!closeAddressForm);
              setIsZipCodeChanged(true);
              sentry.setUser({id: customerId});
              sentry.captureMessage(
                `Distance mismatch during address creation/editing for user - ${customerId}`,
                'error'
              );
              return;
            }
            addressAcceptedLocationMismatchAnalytic({mapLatLng, mapAddressString}, distanceResult);
          }
        } catch (err) {
          sentry.captureException(err);
        }
      }

      const omitAbleKeys = ['provinceName', 'districtName', 'subDistrictName'];
      const createAddressInput = omit(address, omitAbleKeys);
      const isSyncAddress = isSyncAddressEnabled();
      setLoading(true);
      if (createAddressInput) {
        const savedAddress = await onAddOrEditUserAddress(idToken || '', createAddressInput, _addressId);
        if (savedAddress) {
          dispatch(fetchUserAddressesAction());
          const addressesList = await getUserAddresses(idToken, AddressTypeEnum.SHIPPING);
          const relevantAddress = addressesList.find((address) => address.id === savedAddress?.id);
          if (isDirectCheckout && relevantAddress?.user_latitude && relevantAddress?.user_longitude) {
            if (!(await MultiStoreService.isLocationDeliverable(relevantAddress, false))) {
              SendBrowserEvent.showErrorToast(undefined, t('message.undeliverableAddressLocation'));
              onCloseModal(true);
              return;
            }
            const {latitude, longitude} = getCheckoutLatLng(relevantAddress);
            const userAddress: UserAddressWithOptionalCity = {
              ...relevantAddress,
              latitude,
              longitude
            };
            if (isReactNativeView) {
              userAddress.city = isThai ? relevantAddress?.district?.nameTh : relevantAddress?.district?.nameEn;
            }
            SendBrowserEvent.handleDirectCheckout(userAddress, isCheckoutV3);
          }
          dispatch(setSelectedAddressAction(relevantAddress));
          !isSyncAddress && localStorage.setItem('selectedShippingAddressId', relevantAddress?.id || '');
          if (
            relevantAddress &&
            (address.zip !== selectedShippingAddress?.zip ||
              address.subDistrictName !== selectedShippingAddress?.subDistrict?.nameEn)
          ) {
            await updateStoresForBasket(relevantAddress);
            dispatch(setBasketQuantityChangedProductMapAction({}));
            SendBrowserEvent.updateZipAndSubdistricts(relevantAddress);
            if (isSyncAddress) {
              SendBrowserEvent.updateMetaDataZipAndSubdistricts(relevantAddress);
            }
          }
          if (closeAddressForm) {
            handleConfirmCallback();
            onCloseModal(true);
          } else {
            const {firstName, lastName, email, mobile} = address;
            setAddressBeingEdited({...addressBeingEdited, firstName, lastName, email, mobile} as UserAddress);
          }

          if (_addressId) {
            addBasketEditAnalytics(relevantAddress as UserAddress, false);
            addAddressAnalytics(ANALYTICS.ADDRESS_EDITED, {address: relevantAddress});
          } else {
            addBasketEditAnalytics(relevantAddress as UserAddress, true);
            addAddressAnalytics(ANALYTICS.ADDRESS_CREATED, {address: relevantAddress});
          }
        }
      }
    } catch (e) {
      sentry.captureException(e);
    } finally {
      setLoading(false);
    }
  };

  const addAddressAnalytics = async (eventName: string, payload: any) => {
    analytics.trackEvent(eventName, {
      addressesTotal: userAddressList?.length,
      zip: currentStore?.zipcode,
      sub: currentStore?.subdistrict,
      storeId: basket?.storeCode || '',
      channel: SUGGESTEDLIST_CHANNELS.MAKRO_PRO,
      segment_source_name: isReactNativeView ? EVENT_SOURCE.APP : EVENT_SOURCE.WEB,
      basketSize: basket?.basketItems?.length || 0,
      onePCount: filter1PItems(basket?.basketItems || []).length || 0,
      threePCount: filter3PItems(basket?.basketItems || []).length || 0,
      ...payload
    });
  };

  const addBasketEditAnalytics = async (address: UserAddress, isNewAddress: boolean) => {
    analytics.trackEvent(ANALYTICS.EDIT_BASKET, {
      sourceEvent: isNewAddress ? BASKET_EDIT_SOURCE_EVENTS.ADD_ADDRESS : BASKET_EDIT_SOURCE_EVENTS.EDIT_ADDRESS,
      basketId: basket?.id || '',
      addressId: address?.id || '',
      itemsCount: basket?.basketItems?.length || '',
      productIds: basket?.basketItems?.map((item: any) => item.productId) || [],
      basketItems: basket?.basketItems?.map((item: any) => item.productId) || [],
      priceBeforeDiscount: basket?.prices?.totalOriginalPrice || '',
      total: basket?.prices?.totalFinalPriceToPay || '',
      delivery: basket?.prices?.shippingFee || '',
      discount: basket?.prices?.roundedDiscountAmount || '',
      storeId: basket?.storeCode || '',
      channel: SUGGESTEDLIST_CHANNELS.MAKRO_PRO,
      segment_source_name: isReactNativeView ? EVENT_SOURCE.APP : EVENT_SOURCE.WEB,
      basketSize: basket?.basketItems?.length || 0,
      onePCount: filter1PItems(basket?.basketItems || []).length || 0,
      threePCount: filter3PItems(basket?.basketItems || []).length || 0
    });
  };

  useEffect(() => {
    addAddressAnalytics(addressBeingEdited?.id ? ANALYTICS.ADDRESS_EDIT_OPEN : ANALYTICS.ADDRESS_NEW_OPEN, {
      address: addressBeingEdited?.id ? addressBeingEdited : undefined
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <AddressFormModal
        address={initialAddress as UserAddress}
        open={modalOpen}
        loading={loading}
        onClose={onCloseModal}
        onSubmit={(values: CreateAddressInputType, closeAddressForm?: boolean) => {
          onChangeAddress(values, true, addressBeingEdited?.id, false, !!closeAddressForm);
        }}
        currentStore={currentStore}
        openFullMapView={openFullMapView}
        setOpenFullMapView={setOpenFullMapView}
        locationsV2={[]}
        selectedLocation={initialPinMapData}
        hasUserMovedMapManually={hasUserMovedMapManually}
      />

      {showLocationMismatchWarning ? (
        <LocationMismatchModal
          open={isZipCodeChanged}
          onClose={() => setIsZipCodeChanged(false)}
          onSave={async () => {
            setIsZipCodeChanged(false);
            await forceChangeAddrRef.current?.();
            forceChangeAddrRef.current = null;
            return;
          }}
        />
      ) : (
        <AddressChangedModal
          open={isZipCodeChanged}
          onClose={() => setIsZipCodeChanged(false)}
          onSave={async () => {
            setIsZipCodeChanged(false);
            await forceChangeAddrRef.current?.();
            forceChangeAddrRef.current = null;
            return;
          }}
        />
      )}
    </>
  );
};

export default AddressFormView;
